import React from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';

const STATUS_LABELS_AR = { pending: 'معلّق', approved: 'معتمد', rejected: 'مرفوض', expired: 'منتهٍ' };
const FILTER_LABELS_AR = { ...STATUS_LABELS_AR, all: 'الكل' };

const ReceiptAttachmentsTab = ({
  isArabic,
  attachments,
  filter,
  setFilter,
  onRefresh,
  onApprove,
  onReject,
}) => {
  const fmt = (d) =>
    d
      ? d.toLocaleDateString(isArabic ? 'ar-AE' : 'en-AE', {
          day: '2-digit',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '-';

  return (
    <Card className="overflow-hidden bg-card border-white/10" data-testid="receipt-attachments-panel">
      <div className="p-4 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-bold">
            {isArabic ? 'ربط العضويات الجديدة بالإيرادات' : 'New Membership ↔ Revenue Attachments'}
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            {isArabic
              ? 'الحلاقون يربطون العضويات الجديدة (<3 أيام) بإيراداتهم لتظهر على إيصالات الزبائن — يجب الاعتماد خلال 7 أيام.'
              : 'Barbers attach new memberships (<3 days) to their revenues; approve within 7 days.'}
          </p>
        </div>
        <div className="flex gap-2">
          {['pending', 'approved', 'rejected', 'expired', 'all'].map((s) => (
            <Button
              key={s}
              size="sm"
              variant={filter === s ? 'default' : 'outline'}
              onClick={() => {
                setFilter(s);
                setTimeout(onRefresh, 50);
              }}
              data-testid={`receipt-att-filter-${s}`}
            >
              {isArabic ? FILTER_LABELS_AR[s] : s}
            </Button>
          ))}
        </div>
      </div>

      {attachments.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground">
          {isArabic ? 'لا توجد طلبات' : 'No requests'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40">
              <tr>
                <th className="text-start py-3 px-3">{isArabic ? 'العضوية' : 'Membership'}</th>
                <th className="text-start py-3 px-3">{isArabic ? 'الاسم' : 'Name'}</th>
                <th className="text-start py-3 px-3">{isArabic ? 'الحلاق' : 'Barber'}</th>
                <th className="text-center py-3 px-3">{isArabic ? 'المبلغ' : 'Amount'}</th>
                <th className="text-center py-3 px-3">{isArabic ? 'النقاط' : 'Points'}</th>
                <th className="text-center py-3 px-3">{isArabic ? 'تاريخ الطلب' : 'Requested'}</th>
                <th className="text-center py-3 px-3">{isArabic ? 'الانتهاء' : 'Expires'}</th>
                <th className="text-center py-3 px-3">{isArabic ? 'الحالة' : 'Status'}</th>
                <th className="text-center py-3 px-3">{isArabic ? 'إجراءات' : 'Actions'}</th>
              </tr>
            </thead>
            <tbody>
              {attachments.map((a) => {
                const expDate = a.expires_at ? new Date(a.expires_at) : null;
                const reqDate = a.requested_at ? new Date(a.requested_at) : null;
                const statusColor =
                  a.status === 'pending'
                    ? 'text-amber-500'
                    : a.status === 'approved'
                    ? 'text-emerald-500'
                    : a.status === 'rejected'
                    ? 'text-red-500'
                    : 'text-muted-foreground';
                return (
                  <tr key={a.id} className="border-t border-white/5">
                    <td className="py-3 px-3 font-mono">#{a.membership_number}</td>
                    <td className="py-3 px-3">{a.member_name || '-'}</td>
                    <td className="py-3 px-3">{a.barber_name || a.requested_by_name}</td>
                    <td className="py-3 px-3 text-center">
                      {Number(a.amount || 0).toFixed(2)} د.إ
                    </td>
                    <td className="py-3 px-3 text-center text-emerald-500 font-bold">
                      +{a.points_earned || 0}
                    </td>
                    <td className="py-3 px-3 text-center text-xs">{fmt(reqDate)}</td>
                    <td className="py-3 px-3 text-center text-xs">{fmt(expDate)}</td>
                    <td className={`py-3 px-3 text-center font-bold ${statusColor}`}>
                      {isArabic ? STATUS_LABELS_AR[a.status] || a.status : a.status}
                    </td>
                    <td className="py-3 px-3 text-center">
                      {a.status === 'pending' ? (
                        <div className="flex gap-1 justify-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs text-emerald-500 hover:bg-emerald-500/10"
                            onClick={() => onApprove(a.id)}
                            data-testid={`approve-receipt-att-${a.id}`}
                          >
                            {isArabic ? 'اعتماد' : 'Approve'}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs text-red-500 hover:bg-red-500/10"
                            onClick={() => {
                              const r = window.prompt(
                                isArabic ? 'سبب الرفض (اختياري):' : 'Rejection reason (optional):'
                              );
                              if (r !== null) onReject(a.id, r);
                            }}
                            data-testid={`reject-receipt-att-${a.id}`}
                          >
                            {isArabic ? 'رفض' : 'Reject'}
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default ReceiptAttachmentsTab;
